import React from "react";
import {
  FormFields,
  useRegistration,
} from "../../../contexts/RegistrationContext";
import "./PackageDetails.scss";
import { OrangeRegistrationInput } from "../Inputs";
import { AgentTermsLink } from "../../TermsAndConditionsLink/TermsAndConditionsLink";
import {
  Checkbox,
  Form,
  Button,
  Typography,
} from "antd";
import {
  CheckOutlined,
  StarOutlined,
  CrownOutlined,
  UserOutlined,
  BankOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import { listRegistrationPlansOrdered } from "../../../utils/agentRegistrationPlans";

const { Text, Paragraph } = Typography;

const iconTypeToComponent = {
  UserOutlined,
  CrownOutlined,
  BankOutlined,
  StarOutlined,
  GiftOutlined,
};

const PLANS = listRegistrationPlansOrdered();

const PackageDetails = ({ missingFields, showPackagesOnly = false }) => {
  const { formData, setFormKey, referralLocked } = useRegistration();

  const renderFeatureRow = (text, key) => (
    <li key={key} className="agent-reg-plan-card__feature-row">
      <CheckOutlined className="agent-reg-plan-card__feature-check" aria-hidden />
      <Text className="agent-reg-plan-card__feature-text">{text}</Text>
    </li>
  );

  const renderPlanCard = (plan) => {
    const isSelected = formData[FormFields.ADDITIONAL_INFO.PACKAGE] === plan.code;
    const isRecommended = plan.code === "PRO";
    const isTrial = plan.code === "TRIAL";
    const IconCmp = iconTypeToComponent[plan.iconType] || StarOutlined;
    const features = Array.isArray(plan.features) ? plan.features : [];
    const extraSections = Array.isArray(plan.extraSections) ? plan.extraSections : [];

    return (
      <article
        key={plan.code}
        className={[
          "agent-reg-plan-card",
          isRecommended ? "agent-reg-plan-card--recommended" : "",
          isTrial ? "agent-reg-plan-card--trial" : "",
          isSelected ? "agent-reg-plan-card--selected" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ ["--plan-accent"]: plan.accent }}
      >
        {plan.badge ? (
          <span className="agent-reg-plan-card__badge">{plan.badge}</span>
        ) : null}

        <div className="agent-reg-plan-card__top">
          <div className="agent-reg-plan-card__icon" aria-hidden>
            <IconCmp />
          </div>
          <h3 className="agent-reg-plan-card__title">{plan.name}</h3>
          <p className="agent-reg-plan-card__subtitle">
            {isTrial ? "להתחיל בלי סיכון" : plan.subtitle}
          </p>
        </div>

        <div className="agent-reg-plan-card__pricing">
          <p className="agent-reg-plan-card__price">{plan.priceMain}</p>
          {plan.priceVatLine ? (
            <p className="agent-reg-plan-card__price-meta">{plan.priceVatLine}</p>
          ) : null}
          <p className="agent-reg-plan-card__no-commitment">{plan.noCommitment}</p>
        </div>

        <div className="agent-reg-plan-card__body">
          <ul className="agent-reg-plan-card__features">
            {features.map((text, index) => renderFeatureRow(text, `f-${index}`))}
          </ul>

          {extraSections.map((section, sectionIndex) => (
            <div
              key={`extra-${sectionIndex}`}
              className="agent-reg-plan-card__extra"
            >
              {section.title ? (
                <p className="agent-reg-plan-card__extra-title">{section.title}</p>
              ) : null}
              <ul className="agent-reg-plan-card__extra-lines">
                {(Array.isArray(section.lines) ? section.lines : []).map(
                  (line, lineIndex) =>
                    renderFeatureRow(line, `e-${sectionIndex}-${lineIndex}`)
                )}
              </ul>
            </div>
          ))}
        </div>

        <Button
          type={isSelected ? "primary" : "default"}
          size="middle"
          icon={isSelected ? <CheckOutlined /> : null}
          onClick={() => setFormKey(FormFields.ADDITIONAL_INFO.PACKAGE, plan.code)}
          block
          className="agent-reg-plan-card__cta"
        >
          {isSelected ? "נבחר" : plan.ctaLabel}
        </Button>
      </article>
    );
  };

  const packagesBlock = (
    <div
      className={`register-packages-wrapper${showPackagesOnly ? " register-packages-wrapper--fullwidth" : ""}`}
      dir="rtl"
    >
      <div className="register-packages-heading-block">
        <p className="register-packages-heading">בחרו איך להתחיל</p>
        <p className="register-packages-subheading">
          אפשר להתחיל בניסיון בלי כרטיס — או לבחור חבילה ולצאת לדרך.
        </p>
      </div>
      <div className="register-packages-row">{PLANS.map((plan) => renderPlanCard(plan))}</div>
    </div>
  );

  if (showPackagesOnly) {
    return packagesBlock;
  }

  return (
    <div className="agent-reg-package-stage">
      {packagesBlock}
      <div className="agent-reg-package-meta">
        <Form layout="vertical" className="agent-reg-fields-form agent-reg-package-meta__form" dir="rtl">
          <OrangeRegistrationInput
            registrationKey={FormFields.ADDITIONAL_INFO.RECOMMENDED_BY}
            missingFields={missingFields}
          />
          {referralLocked ? (
            <Paragraph className="agent-reg-package-meta__referral">
              הקוד נקלט מהלינק
            </Paragraph>
          ) : null}
          <Form.Item
            validateStatus={
              missingFields.includes(FormFields.OTHERS.TERMS_APPROVED) ? "error" : ""
            }
            help={
              missingFields.includes(FormFields.OTHERS.TERMS_APPROVED) ? (
                <span>יש לאשר את התקנון</span>
              ) : null
            }
            className="agent-reg-package-meta__terms"
          >
            <Checkbox
              onChange={(e) => setFormKey(FormFields.OTHERS.TERMS_APPROVED, e.target.checked)}
              checked={formData[FormFields.OTHERS.TERMS_APPROVED]}
            >
              לצפייה ואישור ב<AgentTermsLink />
            </Checkbox>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default PackageDetails;
